import { ActionIcon } from '@/components'
import { I18N, variantProvider } from '@/app'
import { ProviderId } from '@/features/Auth/providers/types'

type SocialButtonProps = {
  platform: Exclude<ProviderId, 'email' | 'customToken'>
  handleSocial: (platform: SocialButtonProps['platform']) => void
}

// The provider id is lowercase because the machine's ids are; the name a person reads is
// not derivable from it — `capitalize('linkedin')` gives "Linkedin".
const DISPLAY_NAME: Record<SocialButtonProps['platform'], string> = {
  google: 'Google',
  facebook: 'Facebook',
  apple: 'Apple',
  linkedin: 'LinkedIn'
}

export const SocialButton = ({ platform, handleSocial }: SocialButtonProps) => {
  const _platform = DISPLAY_NAME[platform]

  const platformButtonStyle = styles?.[`button${_platform}`]

  return (
    <ActionIcon
      debugName={`socialButton:${_platform}`}
      css={[styles.button, platformButtonStyle]}
      variants={['originalColor']}
      icon={platform.toLowerCase()}
      onPress={() => handleSocial(platform)}
      tabIndex={0}
      aria-label={I18N.t('accessibility.socialLogin', { provider: _platform })}
      iconProps={{
        color: platformButtonStyle?.color,
        size: ICON_SIZE * (platform === 'facebook' ? 1.5 : 1)
      }}
    />
  )
}

const ICON_SIZE = 24

const BUTTON_HEIGHT = 48
const BUTTON_WIDTH = 68
const BORDER_WIDTH = 1
const FACEBOOK_ICON_COLOR = '#1877F2'
const LINKEDIN_ICON_COLOR = '#006699'

const styles = variantProvider.createComponentStyle(
  theme => ({
    button: {
      flex: 1,
      height: BUTTON_HEIGHT,
      width: BUTTON_WIDTH,
      ...theme.spacing.padding(2),
      borderRadius: theme.borderRadius.tiny,
      backgroundColor: theme.colors.transparent,
      ...theme.presets.center,
      transition: 'background 0.2s',

      '&:hover': {
        backgroundColor: theme.colors.neutral2
      }
    },
    buttonGoogle: {
      ...theme.border.neutral5({ width: BORDER_WIDTH })
    },
    buttonFacebook: {
      color: FACEBOOK_ICON_COLOR,
      ...theme.border.create({ color: FACEBOOK_ICON_COLOR, width: BORDER_WIDTH })
    },
    buttonApple: {
      color: theme.colors.neutral10,
      ...theme.border.neutral10({ width: BORDER_WIDTH })
    },
    buttonLinkedIn: {
      color: LINKEDIN_ICON_COLOR,
      ...theme.border.create({ color: LINKEDIN_ICON_COLOR, width: BORDER_WIDTH })
    }
  }),
  true
)
